import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from '../../common/FeatherIcon';
import { canViewBilling, isPrivilegedUser } from '../../../utils/permissions';
import { readNormalizedUserFromStorage } from '../../../utils/entitlements';
import usePillarNav from '../Sidebar/pillarNav';

/**
 * The account button (initials avatar, name over organisation, chevron) and
 * its menu. Built from the person's initials so it works without photos; the
 * teal dot says the session is live. On narrow screens the button collapses to
 * the avatar alone (Header.css).
 *
 * Every item leads somewhere real: the three profile routes, Billing & Usage
 * for the org's administrators, and sign out.
 *
 * Billing lives here, not in the sidebar: it is the account's bill, not a
 * module. It is offered only to whoever the /billing route admits
 * (requiredRole="Administrator") and who has an organization to bill —
 * canViewBilling in utils/permissions.
 *
 * "New navigation" (PILLAR_NAV, development organisation only): a switch for the
 * pillar sidebar. While it is on, the menu is Profile and — for administrators —
 * Settings: the account pages and every administration page are tabs on those two
 * (Sidebar/pillarTabs.js `profile`, `settings`).
 */
const MENU_ITEMS = '[role="menuitem"], [role="menuitemcheckbox"]';

const AccountMenu = ({ identity, onSignOut }) => {
  const { t: translate } = useTranslation(['layout', 'nav']);
  const ta = (key, opts) => translate(`layout:headerAccount.${key}`, opts);
  const pillarNav = usePillarNav();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const items = () => Array.from(menuRef.current?.querySelectorAll(MENU_ITEMS) || []);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Land on the first item when the menu opens, as a menu button should.
  useEffect(() => {
    if (open) menuRef.current?.querySelector(MENU_ITEMS)?.focus();
  }, [open]);

  const onMenuKey = (e) => {
    const list = items();
    const i = list.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); close(true); return; }
    if (e.key === 'Tab') { close(false); return; }
    let next = null;
    if (e.key === 'ArrowDown') next = list[(i + 1) % list.length];
    if (e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
    if (e.key === 'Home') next = list[0];
    if (e.key === 'End') next = list[list.length - 1];
    if (next) { e.preventDefault(); next.focus(); }
  };

  const onButtonKey = (e) => {
    if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); }
    if (e.key === 'Escape' && open) { e.preventDefault(); close(true); }
  };

  const { name, email, hasName, initials, role, org } = identity;

  // Read when the menu draws, so a role change after sign-in is picked up.
  const billing = canViewBilling();

  // Settings is for whoever the classic Management section admits.
  const isAdmin = isPrivilegedUser(readNormalizedUserFromStorage());
  const links = pillarNav.on ? [
    { to: '/profile', icon: 'user', label: ta('profile') },
    { to: '/admin/api-keys', icon: 'key', label: ta('apiKeys') },
    ...(isAdmin ? [{ to: '/admin/users', icon: 'settings', label: translate('nav:pillarTabs.groups.settings') }] : []),
  ] : [
    { to: '/profile', icon: 'user', label: ta('profile') },
    { to: '/profile/security', icon: 'lock', label: ta('signInSecurity') },
    { to: '/admin/api-keys', icon: 'key', label: ta('apiKeys') },
    ...(billing ? [{ to: '/billing', icon: 'credit-card', label: ta('billing') }] : []),
  ];

  return (
    <div className="hdr-acct" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`hdr-acct-btn${open ? ' is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="hdr-acct-menu"
        aria-label={ta('openMenuFor', { name: name || email })}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onButtonKey}
      >
        <span className="hdr-acct-avatar" aria-hidden="true">
          {initials}
          <span className="hdr-acct-live" title={ta('sessionLive')} />
        </span>
        <span className="hdr-acct-text" aria-hidden="true">
          <span className="hdr-acct-name">{name}</span>
          {org && <span className="hdr-acct-org">{org}</span>}
        </span>
        <FeatherIcon name="chevron-down" size={12} className="hdr-acct-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div
          id="hdr-acct-menu"
          className="hdr-acct-menu"
          role="menu"
          aria-label={ta('menuLabel')}
          ref={menuRef}
          onKeyDown={onMenuKey}
        >
          <div className="hdr-acct-head">
            <span className="hdr-acct-avatar is-lg" aria-hidden="true">{initials}</span>
            <span className="hdr-acct-who">
              <span className="hdr-acct-who-name">{name}</span>
              {hasName && email && <span className="hdr-acct-who-email">{email}</span>}
              {(role || org) && (
                <span className="hdr-acct-chips">
                  {role && <span className="hdr-acct-chip is-role">{role}</span>}
                  {org && <span className="hdr-acct-chip">{org}</span>}
                </span>
              )}
            </span>
          </div>
          <div className="hdr-acct-items">
            {links.map((l) => (
              <Link key={l.to} to={l.to} role="menuitem" tabIndex={-1} className="hdr-acct-item" onClick={() => close(false)}>
                <FeatherIcon name={l.icon} size={16} aria-hidden="true" />
                <span>{l.label}</span>
              </Link>
            ))}
          </div>
          {pillarNav.available && (
            <div className="hdr-acct-items">
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={pillarNav.on}
                tabIndex={-1}
                className="hdr-acct-item hdr-acct-switch"
                onClick={() => pillarNav.setOn(!pillarNav.on)}
              >
                <FeatherIcon name="layout" size={16} aria-hidden="true" />
                <span className="hdr-acct-switch-text">
                  <span>{translate('nav:pillarSwitch.label')}</span>
                  <span className="hdr-acct-switch-hint">{translate('nav:pillarSwitch.hint')}</span>
                </span>
                <span className={`hdr-acct-toggle${pillarNav.on ? ' is-on' : ''}`} aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="hdr-acct-items is-foot">
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              id="logout-button"
              className="hdr-acct-item is-danger"
              onClick={() => { close(false); onSignOut(); }}
            >
              <FeatherIcon name="log-out" size={16} aria-hidden="true" />
              <span>{ta('signOut')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
